export const GCP_COURSES = [
  {
    id: 'gcp-fundamentals',
    title: 'Google Cloud Platform Fundamentals',
    description: 'Master Google Cloud Platform services including Compute Engine, Cloud Storage, and App Engine.',
    level: 'beginner',
    totalDuration: '10-12 hours',
    image: '/api/placeholder/400/300',
    prerequisites: ['Basic cloud computing concepts', 'Linux command line basics'],
    learningOutcomes: [
      'Navigate Google Cloud Console effectively',
      'Deploy applications using Compute Engine',
      'Manage data with Cloud Storage and databases',
      'Implement CI/CD with Cloud Build',
      'Monitor applications with Cloud Operations',
      'Secure GCP resources with IAM'
    ],
    modules: [
      {
        id: 'gcp-introduction',
        title: 'GCP Introduction & Setup',
        description: 'Getting started with Google Cloud Platform and understanding its core services',
        duration: '90 minutes',
        difficulty: 'beginner',
        objectives: [
          'Create and configure GCP account',
          'Navigate Google Cloud Console',
          'Understand GCP global infrastructure',
          'Use Google Cloud SDK and CLI'
        ],
        content: `# Google Cloud Platform Introduction

## What is Google Cloud Platform?

Google Cloud Platform (GCP) is a suite of cloud computing services that runs on the same infrastructure that Google uses internally for its end-user products.

### Key Benefits:
- **Global Infrastructure**: 200+ countries and territories
- **Security**: Enterprise-grade security by default
- **Innovation**: Access to Google's cutting-edge technology
- **Cost-effective**: Pay-as-you-go pricing model
- **Integration**: Seamless integration with Google services

## Core GCP Services

### Compute Services
- **Compute Engine**: Virtual machines in the cloud
- **App Engine**: Platform-as-a-Service for web applications
- **Cloud Functions**: Serverless computing platform
- **Google Kubernetes Engine (GKE)**: Managed Kubernetes clusters
- **Cloud Run**: Containerized applications

### Storage & Databases
- **Cloud Storage**: Object storage service
- **Cloud SQL**: Managed relational databases
- **Firestore**: NoSQL document database
- **BigQuery**: Data warehouse and analytics
- **Cloud Spanner**: Globally distributed database

### Networking
- **VPC**: Virtual Private Cloud
- **Cloud Load Balancing**: Distribute traffic
- **Cloud CDN**: Content delivery network
- **Cloud DNS**: Domain name system

## Getting Started with GCP

### Account Setup
\`\`\`bash
# Install Google Cloud SDK
curl https://sdk.cloud.google.com | bash
exec -l $SHELL

# Initialize gcloud
gcloud init

# Authenticate
gcloud auth login

# Set default project
gcloud config set project YOUR_PROJECT_ID
\`\`\`

### Basic CLI Commands
\`\`\`bash
# List projects
gcloud projects list

# Set active project
gcloud config set project PROJECT_ID

# List compute instances
gcloud compute instances list

# Create a new VM instance
gcloud compute instances create my-vm \\
    --zone=us-central1-a \\
    --machine-type=e2-micro \\
    --image-family=debian-11 \\
    --image-project=debian-cloud

# SSH into instance
gcloud compute ssh my-vm --zone=us-central1-a
\`\`\`

## Creating Your First Application

### Deploy a Simple Web App to App Engine
\`\`\`yaml
# app.yaml
runtime: python39

handlers:
- url: /.*
  script: auto
\`\`\`

\`\`\`python
# main.py
from flask import Flask

app = Flask(__name__)

@app.route('/')
def hello():
    return 'Hello, Google Cloud Platform!'

@app.route('/health')
def health():
    return {'status': 'healthy', 'service': 'gcp-demo'}

if __name__ == '__main__':
    app.run(debug=True, host='0.0.0.0', port=8080)
\`\`\`

\`\`\`bash
# Deploy to App Engine
gcloud app deploy

# View your application
gcloud app browse
\`\`\`

## Cloud Storage Operations

### Using gsutil
\`\`\`bash
# Create a bucket
gsutil mb gs://your-unique-bucket-name

# Upload files
gsutil cp local-file.txt gs://your-bucket/

# Download files
gsutil cp gs://your-bucket/file.txt ./

# List bucket contents
gsutil ls gs://your-bucket/

# Sync directories
gsutil rsync -r ./local-folder gs://your-bucket/folder
\`\`\`

### Cloud Storage with Python
\`\`\`python
from google.cloud import storage

def upload_blob(bucket_name, source_file_name, destination_blob_name):
    """Uploads a file to the bucket."""
    storage_client = storage.Client()
    bucket = storage_client.bucket(bucket_name)
    blob = bucket.blob(destination_blob_name)

    blob.upload_from_filename(source_file_name)
    print(f"File {source_file_name} uploaded to {destination_blob_name}.")

def download_blob(bucket_name, source_blob_name, destination_file_name):
    """Downloads a blob from the bucket."""
    storage_client = storage.Client()
    bucket = storage_client.bucket(bucket_name)
    blob = bucket.blob(source_blob_name)

    blob.download_to_filename(destination_file_name)
    print(f"Blob {source_blob_name} downloaded to {destination_file_name}.")
\`\`\``,
        practice: {
          task: 'Deploy a full-stack web application to GCP using App Engine, Cloud Storage, and Cloud SQL',
          starter: `# Create a web application that:
# 1. Serves static files from Cloud Storage
# 2. Stores user data in Cloud SQL
# 3. Deploys via App Engine

# app.yaml
runtime: python39

# Add your configuration here`,
          hints: [
            'Use Cloud SQL for PostgreSQL database',
            'Store uploaded files in Cloud Storage buckets',
            'Configure environment variables in app.yaml',
            'Use Cloud Build for automated deployment',
            'Implement health checks for monitoring'
          ]
        },
        quiz: {
          questions: [
            {
              id: 'gcp-1',
              question: 'Which GCP service is best for hosting a containerized web application with automatic scaling?',
              options: ['Compute Engine', 'App Engine', 'Cloud Run', 'Cloud Functions'],
              correctAnswer: 2,
              difficulty: 'medium' as const,
              explanation: 'Cloud Run is designed for containerized applications with automatic scaling and pay-per-request pricing.',
              points: 15
            }
          ],
          passThreshold: 80
        }
      }
    ],
    type: 'gcp'
  },
  {
    id: 'gcp-data-analytics',
    title: 'GCP Data & Analytics',
    description: 'Master BigQuery, Dataflow, and ML services for data processing and analytics on Google Cloud.',
    level: 'intermediate',
    totalDuration: '8-10 hours',
    image: '/api/placeholder/400/300',
    prerequisites: ['SQL knowledge', 'Basic data analysis concepts', 'GCP fundamentals'],
    learningOutcomes: [
      'Query large datasets with BigQuery',
      'Build data pipelines with Dataflow',
      'Implement ML models with Vertex AI',
      'Visualize data with Data Studio',
      'Process streaming data in real-time',
      'Optimize data warehouse performance'
    ],
    modules: [
      {
        id: 'bigquery-analytics',
        title: 'BigQuery Data Analytics',
        description: 'Advanced analytics and data warehousing with Google BigQuery',
        duration: '120 minutes',
        difficulty: 'intermediate',
        objectives: [
          'Master BigQuery SQL and functions',
          'Design efficient data warehouse schemas',
          'Implement advanced analytics queries',
          'Optimize query performance and costs'
        ],
        content: `# BigQuery Data Analytics

## BigQuery Overview

BigQuery is Google's fully managed, serverless data warehouse that enables scalable analysis over petabytes of data.

### Key Features:
- **Serverless**: No infrastructure management
- **Fast**: Query terabytes in seconds
- **Scalable**: Automatic scaling
- **Cost-effective**: Pay for what you use
- **Standard SQL**: Familiar syntax

## Advanced SQL Queries

### Window Functions
\`\`\`sql
-- Running totals
SELECT 
  date,
  revenue,
  SUM(revenue) OVER (
    ORDER BY date 
    ROWS BETWEEN UNBOUNDED PRECEDING AND CURRENT ROW
  ) AS running_total
FROM sales_data
ORDER BY date;

-- Ranking and percentiles
SELECT 
  product_name,
  sales_amount,
  RANK() OVER (ORDER BY sales_amount DESC) as sales_rank,
  PERCENT_RANK() OVER (ORDER BY sales_amount) as percentile
FROM product_sales;
\`\`\`

### Array and Struct Operations
\`\`\`sql
-- Working with arrays
SELECT 
  user_id,
  interests,
  ARRAY_LENGTH(interests) as interest_count,
  'technology' IN UNNEST(interests) as likes_tech
FROM user_profiles;

-- Struct operations
SELECT 
  order_id,
  customer.name as customer_name,
  customer.email as customer_email,
  shipping.address.city as shipping_city
FROM orders;
\`\`\`

### Time-based Analytics
\`\`\`sql
-- Time series analysis
SELECT 
  DATE_TRUNC(order_date, MONTH) as month,
  COUNT(*) as order_count,
  AVG(total_amount) as avg_order_value,
  LAG(COUNT(*)) OVER (ORDER BY DATE_TRUNC(order_date, MONTH)) as prev_month_orders
FROM orders
WHERE order_date >= DATE_SUB(CURRENT_DATE(), INTERVAL 12 MONTH)
GROUP BY month
ORDER BY month;
\`\`\`

## Data Pipeline with Dataflow

### Apache Beam Pipeline
\`\`\`python
import apache_beam as beam
from apache_beam.options.pipeline_options import PipelineOptions

def run_pipeline():
    pipeline_options = PipelineOptions([
        '--project=your-project-id',
        '--region=us-central1',
        '--runner=DataflowRunner',
        '--temp_location=gs://your-bucket/temp',
        '--staging_location=gs://your-bucket/staging'
    ])

    with beam.Pipeline(options=pipeline_options) as pipeline:
        (
            pipeline
            | 'Read from BigQuery' >> beam.io.ReadFromBigQuery(
                query='SELECT * FROM \`your-project.dataset.table\`',
                use_standard_sql=True
            )
            | 'Transform Data' >> beam.Map(transform_function)
            | 'Write to BigQuery' >> beam.io.WriteToBigQuery(
                'your-project:dataset.output_table',
                schema=output_schema,
                write_disposition=beam.io.BigQueryDisposition.WRITE_TRUNCATE
            )
        )

def transform_function(element):
    # Your transformation logic here
    return {
        'processed_field': element['original_field'].upper(),
        'calculated_value': element['value1'] + element['value2'],
        'timestamp': datetime.now().isoformat()
    }
\`\`\``,
        practice: {
          task: 'Build a complete data analytics pipeline that processes e-commerce data and generates business insights',
          starter: `-- Create analytics queries for:
-- 1. Customer segmentation analysis
-- 2. Product performance metrics
-- 3. Sales trend analysis
-- 4. Revenue forecasting

-- Start with customer analysis
SELECT 
  customer_id,
  -- Add your analysis here
FROM customer_orders;`,
          hints: [
            'Use window functions for trend analysis',
            'Implement customer lifetime value calculations',
            'Create cohort analysis for retention',
            'Build product recommendation queries',
            'Use ML.FORECAST for predictions'
          ]
        },
        quiz: {
          questions: [
            {
              id: 'bigquery-1',
              question: 'Which BigQuery function is used to calculate running totals?',
              options: ['SUM() OVER()', 'RUNNING_SUM()', 'CUMULATIVE_SUM()', 'TOTAL()'],
              correctAnswer: 0,
              difficulty: 'medium' as const,
              explanation: 'SUM() OVER() with appropriate window frame is used for running totals in BigQuery.',
              points: 15
            }
          ],
          passThreshold: 80
        }
      }
    ],
    type: 'gcp'
  }
];